import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Card, CardHeader, CardContent } from "../../../components/ui/card";
import TextController from "../../form-fields/TextController";
import AmountController from "../../form-fields/AmountController";
import DateController from "../../form-fields/DateController";
import SelectController from "../../form-fields/SelectController";
import { Button } from "../../../components/ui/button";
import { Icons } from "../../../utils/icons";
import XanoAPI from "../../../lib/XanoAPI";
import { useToast } from "../../../components/ui/use-toast";
import type { AutoSaveStatus } from "../../../hooks/useIntervalAutoSave";
import { Clock } from "lucide-react";

interface PolicyFormProps {
  client: ClientType;
  formsDirty: any;
  setFormsDirty: Function;
  fetchClientData: Function;
  resetFormsDirtyState: Function;
  saveStatus: AutoSaveStatus;
  lastSaveTime: number;
}

const PolicyForm: React.FC<PolicyFormProps> = ({
  client,
  formsDirty,
  setFormsDirty,
  fetchClientData,
  resetFormsDirtyState,
  saveStatus,
  lastSaveTime,
}) => {
  const api = XanoAPI.getInstance();
  const { toast } = useToast();
  const isAutoSaving = React.useRef(false);
  
  // Real-time auto-save text with live updates
  const [currentTime, setCurrentTime] = useState(Date.now());

  const methods = useForm({
    mode: "onChange", // Enable real-time dirty tracking
    defaultValues: {
      policy_provider: client?.policy_provider || "",
      annuity_type: client?.annuity_type || "",
      rollover_amount: client?.rollover_amount || 0,
      policy_start_date: client?.policy_start_date || "",
      policy_end_date: client?.policy_end_date || "",
      policy_status: client?.policy_status || "",
      agent_commission: client?.agent_commission || 0,
    },
  });

  const { handleSubmit, formState, reset } = methods;
  const { isDirty, dirtyFields, isValid } = formState;

  // Update current time every second for real-time display
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Sync React Hook Form's isDirty state with parent's formsDirty state
  useEffect(() => {
    if (isDirty && dirtyFields) {
      const dirtyFieldNames = Object.keys(dirtyFields);
      setFormsDirty((prev: any) => ({
        ...prev,
        policy: dirtyFieldNames,
      }));
    } else {
      // Clear dirty state when form is clean
      setFormsDirty((prev: any) => ({
        ...prev,
        policy: [],
      }));
    }
  }, [isDirty, dirtyFields]);

  const onSubmit = async (data: any) => {
    const processedData = {
      ...client,
      ...data,
    };

    try {
      await api.updateClient(Number(client.id), processedData);
      
      // Fetch updated data immediately
      await fetchClientData(client.id);

      // Reset form to clear dirty state
      reset(data);

      // Clear the parent's dirty state
      resetFormsDirtyState();

      // Only show toast notification on manual save, not auto-save
      if (!isAutoSaving.current) {
        toast({
          title: "Policy Updated!",
          description: "Policy details have been saved successfully.",
        });
      }
      
      // Reset the auto-save flag
      isAutoSaving.current = false;
    } catch (error) {
      console.error("Error updating policy:", error);
      if (!isAutoSaving.current) {
        toast({
          title: "Error",
          description: "There was an issue saving the policy data.",
          variant: "destructive",
        });
      }
    }
  };

  // Format auto-save time
  const getAutoSaveText = () => {
    if (lastSaveTime === 0) return null;
    
    const timeSinceLastSave = currentTime - lastSaveTime;
    const seconds = Math.floor(timeSinceLastSave / 1000);
    const minutes = Math.floor(seconds / 60);

    if (minutes > 0) {
      return `Autosaved ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else if (seconds > 5) {
      return `Autosaved ${seconds} seconds ago`;
    } else if (seconds > 0) {
      return "Autosaved just now";
    }
    return null;
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="pb-20 my-6">
        <Card className="mb-8" id="policy-form">
          <CardHeader>
            <h4 className="text-lg font-semibold text-gray-700">
              Annuity Policy Details
            </h4>
          </CardHeader>

          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-x-8">
            <TextController
              name="policy_provider"
              label="Policy Provider"
              placeholder="Enter provider name"
              defaultValue={client?.policy_provider || ""}
            />

            <SelectController
              name="annuity_type"
              label="Annuity Type"
              defaultValue={client?.annuity_type || ""}
              options={[
                { value: "", label: "Please select ..." },
                { value: "fixed", label: "Fixed Annuity" },
                { value: "variable", label: "Variable Annuity" },
                { value: "fia", label: "Fixed Indexed Annuity (FIA)" },
                { value: "spia", label: "Immediate Annuity (SPIA)" },
                { value: "dia", label: "Deferred Income Annuity (DIA)" },
                {
                  value: "qlac",
                  label: "Qualified Longevity Annuity Contract (QLAC)",
                },
                {
                  value: "myga",
                  label: "Multi-Year Guaranteed Annuity (MYGA)",
                },
              ]}
            />

            <AmountController
              name="rollover_amount"
              label="Rollover Amount"
              placeholder="Enter amount"
              defaultValue={client?.rollover_amount || 0}
            />

            <DateController
              name="policy_start_date"
              label="Policy Start Date"
              placeholder="MM/DD/YYYY"
              defaultValue={client?.policy_start_date || ""}
            />

            <DateController
              name="policy_end_date"
              label="Policy End Date"
              placeholder="MM/DD/YYYY"
              defaultValue={client?.policy_end_date || ""}
            />

            <SelectController
              name="policy_status"
              label="Policy Status"
              defaultValue={client?.policy_status || ""}
              options={[
                { value: "", label: "Please select ..." },
                { value: "pending", label: "Pending" },
                { value: "active", label: "Active" },
                { value: "cancelled", label: "Cancelled" },
                { value: "completed", label: "Completed" },
              ]}
            />

            <AmountController
              name="agent_commission"
              label="Agent Commission"
              placeholder="Calculated automatically"
              defaultValue={client?.agent_commission || 0}
              readOnly={true}
            />
          </CardContent>
        </Card>

        <div className="fixed bottom-0 right-0 m-3 z-50 flex items-center gap-3">
          {getAutoSaveText() && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 rounded-md border border-gray-200">
              <Clock className="h-3.5 w-3.5 text-gray-500" />
              <span className="text-xs text-gray-600">
                {getAutoSaveText()}
              </span>
            </div>
          )}
          <Button
            type="submit"
            className="p-3 bg-accent"
            disabled={!isValid || !isDirty}
            onClick={(e) => {
              // Check if this is a manual click (has isTrusted property)
              if (e.isTrusted) {
                isAutoSaving.current = false;
              } else {
                // Programmatic click (auto-save)
                isAutoSaving.current = true;
              }
            }}
          >
            <Icons.save className="mr-2" />
            {isDirty ? "Save Changes" : "No Changes"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default PolicyForm;
